import dayjs from "dayjs";

import type { KardexRow } from "./kardexService";
import type { IngredientMovementRow } from "./movementsService";

// One ledger for everything: finished goods (productos), insumos (materia prima)
// and aceites, so a user can search any code/name and see its full movement history
// without first knowing how the item is classified.

export type UnifiedKardexItemKind = "product" | "raw_material" | "oil";

export interface UnifiedKardexFilters {
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
  search?: string;
  // Client-side (applied after merging the two sources):
  itemKind?: UnifiedKardexItemKind;
  location?: string; // bodega (B1/B2/NC) or planta (P1/P2/NC)
}

export interface UnifiedKardexRow {
  id: string;
  fechaSort: number; // ms, for sorting
  fecha: string; // display
  kind: UnifiedKardexItemKind;
  codigo: string | null;
  item: string | null;
  um: string | null;
  ubicacion: string | null; // bodega for products, planta for ingredients
  movimiento: string;
  entrada: number | null;
  salida: number | null;
  saldo: number | null;
  costoUnitario: number | null;
  costoTotal: number | null;
  moneda: string | null;
  referencia: string | null;
  usuario: string | null;
}

export const UNIFIED_KIND_LABELS: Record<UnifiedKardexItemKind, string> = {
  product: "Producto",
  raw_material: "Insumo",
  oil: "Aceite",
};

// Locations across both ledgers (bodegas + plantas). NC exists in both.
export const UNIFIED_LOCATION_OPTIONS = ["B1", "B2", "P1", "P2", "NC"];

const INGREDIENT_MOVEMENT_LABELS: Record<string, string> = {
  reception: "Recepción",
  production_consume: "Consumo Producción",
  adjustment: "Ajuste",
  transfer: "Traslado",
  merma: "Merma",
  maquila_consume: "Consumo Maquila",
};

export function mapKardexRowToUnified(
  r: KardexRow,
  index: number
): UnifiedKardexRow {
  return {
    id: `p-${r.bodega}-${r.codigo}-${r.fecha}-${r.folio ?? ""}-${index}`,
    fechaSort: dayjs(r.fecha).valueOf(),
    fecha: dayjs(r.fecha).format("DD/MM/YYYY"),
    kind: "product",
    codigo: r.codigo,
    item: r.item,
    um: r.um,
    ubicacion: r.bodega,
    movimiento: r.transaccion,
    entrada: r.entrada || null,
    salida: r.salida || null,
    saldo: r.saldo,
    costoUnitario: null,
    costoTotal: null,
    moneda: null,
    referencia:
      [r.docto, r.folio, r.origen_destino].filter(Boolean).join(" · ") || null,
    usuario: r.usuario,
  };
}

export function mapIngredientRowToUnified(
  m: IngredientMovementRow
): UnifiedKardexRow {
  const qty = Number(m.quantity);
  return {
    id: `i-${m.id}`,
    fechaSort: dayjs(m.created_at).valueOf(),
    fecha: dayjs(m.created_at).format("DD/MM/YYYY HH:mm"),
    kind: m.ingredient_type === "oil" ? "oil" : "raw_material",
    codigo: m.ingredient_code,
    item: m.ingredient_name,
    um: m.unit,
    ubicacion: m.plant,
    movimiento: INGREDIENT_MOVEMENT_LABELS[m.movement_type] || m.movement_type,
    entrada: qty > 0 ? qty : null,
    salida: qty < 0 ? Math.abs(qty) : null,
    saldo: m.balance_after,
    costoUnitario: m.unit_cost,
    costoTotal: m.total_cost,
    moneda: m.currency,
    referencia: m.reference_number || m.reference_type || null,
    usuario: m.performed_by_name,
  };
}

export function buildUnifiedRows(
  kardexRows: KardexRow[],
  ingredientRows: IngredientMovementRow[]
): UnifiedKardexRow[] {
  return [
    ...kardexRows.map(mapKardexRowToUnified),
    ...ingredientRows.map(mapIngredientRowToUnified),
  ];
}

export function applyUnifiedClientFilters(
  rows: UnifiedKardexRow[],
  filters: Pick<UnifiedKardexFilters, "itemKind" | "location">
): UnifiedKardexRow[] {
  return rows.filter((r) => {
    if (filters.itemKind && r.kind !== filters.itemKind) return false;
    if (filters.location && r.ubicacion !== filters.location) return false;
    return true;
  });
}
